import React, { useState } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { useVariationsAnalyticStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/variationsAnalyticStore';
import { useUpdateTimePeriods } from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import { useHistory, useMetricFormatter } from 'src/utils/Hooks';
import { getIconForMetricChange } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  VariationsFilter,
  VariationsGaugeData,
  VariationsMetricFormatterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import colors from 'src/utils/colors';

interface VariationsGaugeCardDetailsProps {
  title: string;
  subtitle: React.ReactNode;
  item: VariationsGaugeData;
  overrideSx?: any;
  height?: string;
}

const percentageMetrics: VariationsFilter[] = [
  VariationsFilter.ChildVariationRate,
  VariationsFilter.ChildAccuracy,
  VariationsFilter.ParentPageAccuracy,
  VariationsFilter.PageDensity
];

const VariationsCardDetailsContainer = ({
  children,
  isSelected,
  filterKey,
  onHover,
  overrideSx,
  isClickable = true,
  height = '128px'
}: {
  children: React.ReactNode;
  isSelected: boolean;
  filterKey: VariationsFilter;
  onHover?: (isHovered: boolean) => void;
  overrideSx?: any;
  isClickable?: boolean;
  height?: string;
}) => {
  const history = useHistory();
  const { setMainVariationsFilter } = useVariationsAnalyticStore();
  const availableTimePeriods = useAppSelector((state) => state.mainTimePeriod.availableMainTimePeriods);
  const updateTimePeriods = useUpdateTimePeriods({ history });

  const handleClick = () => {
    if (!isClickable) return;

    try {
      setMainVariationsFilter(filterKey);
      updateTimePeriods(availableTimePeriods.find((period) => period.id === '1w'));
    } catch (error) {
      console.error('Error handling gauge card click:', error);
    }
  };

  return (
    <Flex
      flexDirection="column"
      sx={{
        minHeight: height,
        gap: '4px',
        padding: '16px 24px',
        backgroundColor: isSelected ? '#F2F4F6' : 'inherit',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: isClickable ? '#F2F4F6' : 'inherit'
        },
        ...overrideSx
      }}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      onClick={handleClick}
    >
      {children}
    </Flex>
  );
};

const VariationsCardDetailsChange = ({
  change,
  absoluteChange,
  value,
  valueSuffix,
  valueMetricFormatter = {
    showFullValue: false,
    decimalPlaces: 0,
    valueMetricType: METRICTYPE.VOLUME
  },
  isPercentageMetric = false
}: {
  change?: number;
  absoluteChange?: number;
  value: number;
  valueSuffix?: string;
  valueMetricFormatter?: VariationsMetricFormatterProps;
  isPercentageMetric?: boolean;
}) => {
  const metricFormatter = useMetricFormatter();
  const { showFullValue, decimalPlaces, valueMetricType } = valueMetricFormatter;

  return (
    <Flex alignItems="center">
      <Text variant="subtitle1" sx={{ fontSize: '20px' }}>
        {isPercentageMetric
          ? metricFormatter(value, METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 })
          : metricFormatter(value, valueMetricType, { showFullValue, decimalPlaces })}
      </Text>
      {valueSuffix && (
        <Text variant="subtitle2" sx={{ marginLeft: '8px', fontSize: '12px' }}>
          {valueSuffix}
        </Text>
      )}

      {/* This is on purpose, we have 0, undefined, and non-zero values */}
      {change !== undefined && (
        <div
          style={{
            display: 'flex',
            marginLeft: '8px',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: 500,
            marginTop: 'auto',
            marginBottom: 'auto'
          }}
        >
          <div style={{ marginTop: '3px' }}>{getIconForMetricChange(change, 1)}</div>
          <div style={{ fontSize: '12px', marginLeft: '2px', marginTop: '1px' }}>
            {absoluteChange !== undefined && absoluteChange !== 0 ? (
              <>
                {metricFormatter(Math.abs(absoluteChange), valueMetricType, { showFullValue: false, decimalPlaces: 1 })}
                &nbsp;&#40;
                {metricFormatter(Math.abs(change), METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 })}
                &#41;
              </>
            ) : (
              metricFormatter(Math.abs(change), METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 })
            )}
          </div>
        </div>
      )}
    </Flex>
  );
};

export const VariationsGaugeCardDetails: React.FC<VariationsGaugeCardDetailsProps> = ({
  title,
  subtitle,
  item,
  overrideSx,
  height
}) => {
  const { change, absoluteChange, value, valueSuffix, filterKey, valueMetricFormatter } = item;
  const { variationsFilter } = useVariationsAnalyticStore();
  const [isHovered, setIsHovered] = useState(false);

  const isPercentageMetric = percentageMetrics.includes(filterKey);
  const isSelected = variationsFilter === filterKey;

  const getTextColor = () => {
    if (isSelected) return 'inherit';
    if (isHovered) return colors.darkBlue;
    return colors.grey;
  };

  const handleSubtitleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <VariationsCardDetailsContainer
      isSelected={isSelected}
      filterKey={filterKey}
      onHover={setIsHovered}
      overrideSx={overrideSx}
      isClickable={true}
      height={height}
    >
      <Text
        variant="subtitle3"
        sx={{
          letterSpacing: '0.96px',
          height: '20px',
          WebkitFontSmoothing: 'antialiased',
          marginTop: '8px',
          color: getTextColor()
        }}
      >
        {title}
      </Text>
      <VariationsCardDetailsChange
        change={change}
        absoluteChange={absoluteChange}
        value={value}
        valueSuffix={valueSuffix}
        valueMetricFormatter={valueMetricFormatter}
        isPercentageMetric={isPercentageMetric}
      />
      <div onClick={handleSubtitleClick}>
        <Text variant="body2" lineHeight="20px" sx={{ letterSpacing: '0.08px' }}>
          {subtitle}
        </Text>
      </div>
    </VariationsCardDetailsContainer>
  );
};
